import type { MarkerFeature } from "../../../../../utils/models/MarkerFeature";
import type { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import type { UndoCommand, CommandContext } from "../types";

/**
 * Command for merging two adjacent routes into one.
 * Stores snapshots of both routes and position data for full restoration.
 */
export class MergeRoutesCommand implements UndoCommand {
  readonly type = 'MERGE_ROUTES';
  readonly routeKey: string; // the kept route

  private keepRouteKey: string;
  private removedRouteKey: string;
  private keepRouteMarkersBefore: MarkerFeature[];
  private keepRouteLineStringsBefore: LineStringFeature[];
  private keepRouteMarkersAfter: MarkerFeature[];
  private keepRouteLineStringsAfter: LineStringFeature[];
  private removedRouteSnapshot: any; // Serialized Route (without undo/redo arrays)
  private routePositionsBefore: { key: string; position: number }[];
  private routePositionsAfter: { key: string; position: number }[];
  private activeRouteKeyBefore: string;

  constructor(data: {
    keepRouteKey: string;
    removedRouteKey: string;
    keepRouteMarkersBefore: MarkerFeature[];
    keepRouteLineStringsBefore: LineStringFeature[];
    keepRouteMarkersAfter: MarkerFeature[];
    keepRouteLineStringsAfter: LineStringFeature[];
    removedRouteSnapshot: any;
    routePositionsBefore: { key: string; position: number }[];
    routePositionsAfter: { key: string; position: number }[];
    activeRouteKeyBefore: string;
  }) {
    this.keepRouteKey = data.keepRouteKey;
    this.routeKey = data.keepRouteKey;
    this.removedRouteKey = data.removedRouteKey;
    this.keepRouteMarkersBefore = JSON.parse(JSON.stringify(data.keepRouteMarkersBefore));
    this.keepRouteLineStringsBefore = JSON.parse(JSON.stringify(data.keepRouteLineStringsBefore));
    this.keepRouteMarkersAfter = JSON.parse(JSON.stringify(data.keepRouteMarkersAfter));
    this.keepRouteLineStringsAfter = JSON.parse(JSON.stringify(data.keepRouteLineStringsAfter));
    this.removedRouteSnapshot = JSON.parse(JSON.stringify(data.removedRouteSnapshot));
    this.routePositionsBefore = data.routePositionsBefore;
    this.routePositionsAfter = data.routePositionsAfter;
    this.activeRouteKeyBefore = data.activeRouteKeyBefore;
  }

  undo(ctx: CommandContext): void {
    const keepRoute = ctx.state.routes[this.keepRouteKey];
    if (!keepRoute) return;

    // Restore keep route to before state
    keepRoute.markers = JSON.parse(JSON.stringify(this.keepRouteMarkersBefore));
    keepRoute.lineStrings = JSON.parse(JSON.stringify(this.keepRouteLineStringsBefore));
    keepRoute.updated_at = new Date();

    // Re-add the removed route
    ctx.state.routes[this.removedRouteKey] = JSON.parse(JSON.stringify(this.removedRouteSnapshot));

    // Restore positions
    for (const { key, position } of this.routePositionsBefore) {
      if (ctx.state.routes[key]) {
        ctx.state.routes[key].position = position;
      }
    }

    ctx.state.activeRouteKey = this.activeRouteKeyBefore;
  }

  redo(ctx: CommandContext): void {
    const keepRoute = ctx.state.routes[this.keepRouteKey];
    if (!keepRoute) return;

    // Set keep route to merged state
    keepRoute.markers = JSON.parse(JSON.stringify(this.keepRouteMarkersAfter));
    keepRoute.lineStrings = JSON.parse(JSON.stringify(this.keepRouteLineStringsAfter));
    keepRoute.updated_at = new Date();

    // Remove the other route again
    delete ctx.state.routes[this.removedRouteKey];

    // Restore positions
    for (const { key, position } of this.routePositionsAfter) {
      if (ctx.state.routes[key]) {
        ctx.state.routes[key].position = position;
      }
    }

    ctx.state.activeRouteKey = this.keepRouteKey;
  }
}
